// DOM-freie Bausteine von useRecording (Audit M24) — in Node testbar, weil
// MediaRecorder und MediaStream dort nicht existieren und hier nur über ihre
// Form (Duck-Typing) angesprochen werden.
//
// Befund: Fiel das Headset während der Aufnahme aus, stoppte der Browser den
// MediaRecorder selbst (alle Tracks beendet → `stop`-Event). Ein späterer
// Klick auf Stop setzte `onstop` auf einen bereits inaktiven Recorder und rief
// `stop()` — das Event kam nie, das Promise hing, die Booth blieb in
// „generating". `acquire()` gab außerdem den toten Stream aus dem Cache zurück,
// die nächste Aufnahme war stumm oder scheiterte.

/** Die Teile von MediaRecorder, die wir anfassen. */
export interface RecorderLike {
  readonly state: 'inactive' | 'recording' | 'paused';
  onstop: ((ev: Event) => unknown) | null;
  onerror: ((ev: ErrorEvent) => unknown) | null;
  requestData(): void;
  stop(): void;
}

/** Die Teile von MediaStream, die wir anfassen. */
export interface StreamLike {
  getAudioTracks(): Array<{ readyState: 'live' | 'ended' }>;
}

/** Ist der gehaltene Mic-Stream noch benutzbar? Beendete Tracks (Headset
 *  ab, Gerät gewechselt, Berechtigung entzogen) werden nie wieder live. */
export function isStreamLive(stream: StreamLike | null | undefined): boolean {
  if (!stream) return false;
  const tracks = stream.getAudioTracks();
  return tracks.length > 0 && tracks.every((t) => t.readyState === 'live');
}

/** Wartezeit, bis bereits eingereihte Recorder-Events durch sind. */
export const INACTIVE_SETTLE_MS = 50;

export type RecorderFinish = 'stopped' | 'already-inactive' | 'error' | 'timeout';

export interface FinishOptions {
  /** Wartezeit vor dem Stop, damit der Encoder den letzten Laut noch
   *  aufnimmt (Safari 500 ms, sonst 0). */
  graceMs: number;
  /** Obergrenze fürs Warten auf das `stop`-Event. Danach wird mit dem
   *  weitergemacht, was bis dahin an Chunks da ist. */
  timeoutMs: number;
  setTimer?: (fn: () => void, ms: number) => unknown;
}

/** Stoppt den Recorder und löst IMMER auf — auch wenn er schon inaktiv ist
 *  (Browser hat ihn nach Geräteverlust selbst gestoppt), einen Fehler meldet
 *  oder das `stop`-Event ausbleibt. Die aufgenommenen Chunks sammelt der
 *  Aufrufer über `ondataavailable`; der Browser liefert sie vor `stop`. */
export function finishRecorder(rec: RecorderLike, opts: FinishOptions): Promise<RecorderFinish> {
  const setTimer = opts.setTimer ?? ((fn, ms) => setTimeout(fn, ms));
  return new Promise<RecorderFinish>((resolve) => {
    let done = false;
    const finish = (r: RecorderFinish) => {
      if (done) return;
      done = true;
      rec.onstop = null;
      rec.onerror = null;
      resolve(r);
    };
    // Hat der Browser den Recorder selbst gestoppt, können sein letztes
    // `dataavailable` und `stop` noch in der Event-Queue stehen (der Zustand
    // wird synchron „inactive", die Events kommen als Tasks danach). Kurz
    // warten, damit der letzte Chunk nicht fehlt.
    const settleInactive = () => setTimer(() => finish('already-inactive'), INACTIVE_SETTLE_MS);
    if (rec.state === 'inactive') {
      settleInactive();
      return;
    }
    rec.onstop = () => finish('stopped');
    rec.onerror = () => finish('error');
    const flushAndStop = () => {
      if (done) return;
      if (rec.state === 'inactive') {
        // Während der Gnadenfrist vom Browser gestoppt — das stop-Event ist
        // womöglich schon vorbei.
        settleInactive();
        return;
      }
      // Force a final dataavailable before stop so anything still buffered
      // flushes — Safari otherwise drops the trailing chunk.
      try { rec.requestData(); } catch { /* not all impls expose this */ }
      try {
        rec.stop();
      } catch {
        finish('error');
        return;
      }
      setTimer(() => finish('timeout'), opts.timeoutMs);
    };
    if (opts.graceMs > 0) setTimer(flushAndStop, opts.graceMs);
    else flushAndStop();
  });
}

/** Verständliche Meldung für getUserMedia-/Recorder-Fehler. */
export function describeMicError(e: unknown): string {
  const name = (e as { name?: string } | null)?.name ?? '';
  switch (name) {
    case 'NotAllowedError':
    case 'SecurityError':
      return 'Microphone access was denied. Allow it in the browser settings and try again.';
    case 'NotFoundError':
    case 'OverconstrainedError':
      return 'No microphone found. Connect a microphone and try again.';
    case 'NotReadableError':
    case 'AbortError':
      return 'The microphone is not available — it may be disconnected or in use by another app.';
    default:
      return e instanceof Error && e.message ? e.message : 'The microphone could not be started.';
  }
}

/** Grund, warum eine laufende Aufnahme abgebrochen ist. */
export type RecordingInterruption = 'device-lost' | 'recorder-error';

export const MIC_DISCONNECTED_MESSAGE =
  'The microphone was disconnected — the recording stopped. Reconnect it and record again.';

// ── Steuerlogik von useRecording (rein, ohne MediaRecorder/DOM) ─────────────

/** MIME-Präferenz der Aufnahme. Safari zuerst mp4/AAC: dessen webm/opus-Pfad
 *  (seit 17.1) nimmt zwar auf, spielt aber unzuverlässig ab (stille
 *  Take-Karten, Brummen im STS-Roundtrip). Chrome/Edge: webm/opus nativ.
 *  `''` = Browser-Default. */
export function pickRecorderMime(isTypeSupported: (m: string) => boolean, safari: boolean): string {
  const candidates = safari
    ? ['audio/mp4', 'audio/mp4;codecs=mp4a.40.2', 'audio/webm;codecs=opus', 'audio/webm', '']
    : ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', ''];
  for (const m of candidates) {
    if (!m || isTypeSupported(m)) return m;
  }
  return '';
}

/** getUserMedia-Constraints: bestimmtes Gerät exakt, sonst Default. */
export function micConstraints(deviceId: string | null): MediaStreamConstraints {
  return deviceId ? { audio: { deviceId: { exact: deviceId } } } : { audio: true };
}

/** Gecachten Stream wiederverwenden? Nur für dasselbe Gerät und nur, solange
 *  er lebt (nach getrenntem Headset sind die Tracks `ended`, Audit M24). */
export function canReuseStream(
  cached: StreamLike | null,
  cachedDeviceId: string | null,
  wantDeviceId: string | null,
): boolean {
  return !!cached && cachedDeviceId === wantDeviceId && isStreamLive(cached);
}

/** enumerateDevices → Mikrofonliste. Einträge ohne deviceId (transiente
 *  virtuelle Geräte) fallen raus; ohne Berechtigung fehlen die Labels →
 *  „Microphone N" (N = Position in der ungefilterten Liste, wie bisher). */
export function toAudioInputs(
  devices: ReadonlyArray<{ kind: string; deviceId: string; label: string }>,
): { deviceId: string; label: string }[] {
  return devices
    .filter((d) => d.kind === 'audioinput' && d.deviceId)
    .map((d, i) => ({ deviceId: d.deviceId, label: d.label || `Microphone ${i + 1}` }));
}

/** Anzahl zu verwerfender Samples beim Head-Trim (FB#27), geklemmt auf
 *  [0, length]. */
export function headTrimSamples(headSeconds: number, sampleRate: number, length: number): number {
  return Math.max(0, Math.min(length, Math.floor(headSeconds * sampleRate)));
}
